import { Link, useLocation } from 'react-router-dom';

import {
  ActionIcon,
  AppShell,
  Box,
  Burger,
  Flex,
  Group,
  NavLink,
  Stack,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconActivityHeartbeat,
  IconDeviceDesktop,
  IconHomeLink,
  IconLogout,
  IconMoon,
  IconNetwork,
  IconSunFilled,
  IconSwimming,
} from '@tabler/icons-react';

import { useAuthContext } from '../../hooks/api/AuthProvider';
import { Logo } from './logo';

type Props = {
  children: React.ReactNode;
};

export function AppContainer({ children }: Props) {
  const [opened, { toggle }] = useDisclosure();
  const { isAuthenticated, logout } = useAuthContext();
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const location = useLocation();
  const { colors } = useMantineTheme();

  const buttonStyle = 'filled';
  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 200,
        breakpoint: 'sm',
        collapsed: { mobile: !opened },
      }}
      padding="md"
      styles={(theme) => ({
        header: {
          background:
            colorScheme === 'dark'
              ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderBottom: `1px solid ${colorScheme === 'dark' ? theme.colors.slate[6] : theme.colors.slate[2]}`,
          backdropFilter: 'blur(12px)',
          boxShadow:
            colorScheme === 'dark'
              ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.1)'
              : '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
        },
        navbar: {
          background:
            colorScheme === 'dark'
              ? 'linear-gradient(180deg, #334155 0%, #1e293b 100%)'
              : 'linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)',
          borderRight: `1px solid ${colorScheme === 'dark' ? theme.colors.slate[6] : theme.colors.slate[2]}`,
          backdropFilter: 'blur(12px)',
        },
        main: {
          background:
            colorScheme === 'dark'
              ? 'linear-gradient(135deg, #0f172a 0%, #020617 100%)'
              : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          minHeight: '100vh',
        },
      })}
    >
      <AppShell.Header>
        <Flex justify="space-between" h="100%" align="center" px="lg">
          <Burger
            opened={opened}
            onClick={toggle}
            hiddenFrom="sm"
            size="sm"
            lineSize={2}
            color={colorScheme === 'dark' ? colors.gray[0] : colors.gray[8]}
          />

          <Flex p={3} h="100%">
            <Logo />
          </Flex>

          <Group gap="sm">
            <ActionIcon
              variant="gradient"
              gradient={
                colorScheme === 'dark'
                  ? { from: 'tealish.4', to: 'tealish.6', deg: 45 }
                  : { from: 'tealish.5', to: 'tealish.7', deg: 45 }
              }
              size="lg"
              aria-label="Toggle color scheme"
              onClick={() =>
                setColorScheme(colorScheme === 'dark' ? 'light' : 'dark')
              }
            >
              {colorScheme === 'dark' ? (
                <IconSunFilled size={18} />
              ) : (
                <IconMoon size={18} />
              )}
            </ActionIcon>
            {isAuthenticated && (
              <ActionIcon
                variant="gradient"
                gradient={{ from: 'accent.5', to: 'accent.7', deg: 45 }}
                size="lg"
                aria-label="Logout"
                onClick={logout}
              >
                <IconLogout size={18} />
              </ActionIcon>
            )}
          </Group>
        </Flex>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <Stack gap="sm">
          <NavLink
            component={Link}
            to="/ducks"
            label="Ducks"
            leftSection={<IconDeviceDesktop size={20} />}
            active={location.pathname === '/ducks'}
            variant={location.pathname === '/ducks' ? 'filled' : 'light'}
            style={{
              borderRadius: '12px',
              fontWeight: location.pathname === '/ducks' ? 600 : 500,
            }}
          />
          <NavLink
            component={Link}
            to="/network"
            label="Network"
            leftSection={<IconHomeLink size={20} />}
            variant={location.pathname === '/network' ? 'filled' : 'light'}
            style={{
              borderRadius: '12px',
              fontWeight: location.pathname === '/network' ? 600 : 500,
            }}
          >
            <NavLink
              component={Link}
              to="/network/subnets"
              label="Subnets"
              leftSection={<IconNetwork size={20} />}
              active={location.pathname === '/network/subnets'}
              variant={
                location.pathname === '/network/subnets' ? 'filled' : 'light'
              }
              style={{
                borderRadius: '12px',
                fontWeight:
                  location.pathname === '/network/subnets' ? 600 : 500,
              }}
            />
            <NavLink
              component={Link}
              to="/network/pools"
              label="Pools"
              leftSection={<IconSwimming size={20} />}
              active={location.pathname === '/network/pools'}
              variant={
                location.pathname === '/network/pools' ? 'filled' : 'light'
              }
              style={{
                borderRadius: '12px',
                fontWeight: location.pathname === '/network/pools' ? 600 : 500,
              }}
            />
          </NavLink>
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        <Box p="md">{children}</Box>
      </AppShell.Main>
    </AppShell>
  );
}

export default AppContainer;
